import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import _isEmpty from 'lodash-es/isEmpty';
import Button from 'sushiweb/Button';
import ChevronLeft from 'sushiweb/Icons/all/ChevronLeft';
import ChevronRight from 'sushiweb/Icons/all/ChevronRight';

import {
  PageWrapper,
  PageHeaderContainer,
  PageHeader,
  PageTitle,
  PageSubtitle,
  SectionHeading,
  ContentContainer,
  FooterWrapper,
  FullFooter,
} from '../../styles';

import { formatDate } from '../../../../helpers';
import { getProgramBody } from '../../common/helpers/inputHandlers';
import PlanSnippet from '../../common/Components/PlanSnippet/PlanSnippet';
import StepBar from '../../common/Components/StepBar/StepBar';
import { createPlanSteps } from '../../common/Components/constants';
import {
  CREATE_POLICY_ROUTE,
  PROGRAMS_ROUTE,
} from '../../../../constants/routes';
import {
  getGroupsData,
  createInitialFormData,
  createFormDataFromProgramInfo,
  isFormValid,
  validateDates,
  createProgramBody,
} from './util';

function CreatePlan({
  companyId,
  currentFlow,
  selectedWorkspace,
  groupsData,
  reviewProgramDetails,
  fetchingProgramInfo,
  fetchedProgramInfo,
  programInfo,
  getProgramInfo,
  getAllGroups,
  reviewProgram,
  showErrorToast,
  reviewingProgram,
  clearProgramInfo,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [flag, setFlag] = useState(false);
  const { programId } = useParams();
  const [createPolicyEnabled, setCreatePolicyEnabled] = useState(false);
  const { groups = [] } = groupsData;

  const goBack = () => {
    navigate(PROGRAMS_ROUTE);
  };

  // Fresh "Create meal plan" flow (no :programId in the route): wipe any
  // programInfo/reviewProgramDetails left in Redux from a previously viewed
  // plan or an abandoned creation, otherwise the stale program_id gets reused
  // and an existing program is mutated instead of a new one being created.
  useEffect(() => {
    if (!programId) {
      clearProgramInfo();
    }
  }, [programId]);

  useEffect(() => {
    if (selectedWorkspace?.workspace_id) {
      getAllGroups({
        companyId,
        workspaceId: selectedWorkspace?.workspace_id ?? '',
        flowType: currentFlow,
      });
    }
  }, [selectedWorkspace?.workspace_id, currentFlow]);

  useEffect(() => {
    if (
      selectedWorkspace?.workspace_id &&
      programId &&
      programId !== ''
    ) {
      getProgramInfo({
        programId,
        companyId,
        workspaceId: selectedWorkspace?.workspace_id,
      });
    }
  }, [programId, companyId, selectedWorkspace?.workspace_id]);

  const { allGroups, selectedGroups, unSelectedGroups } = getGroupsData(
    groups,
    programId ? programInfo : null,
  );

  useEffect(() => {
    if (
      programId &&
      programInfo &&
      typeof programInfo?.program_status === 'number' &&
      programInfo?.program_status !== 0
    ) {
      navigate(PROGRAMS_ROUTE, { replace: true });
    }
  }, [programId, programInfo?.program_status, location.pathname]);

  const [formData, setFormData] = useState(
    createInitialFormData(
      programId,
      selectedGroups,
      unSelectedGroups,
      selectedWorkspace,
      currentFlow,
    ),
  );

  useEffect(() => {
    if (
      programId &&
      programId !== '' &&
      !fetchingProgramInfo &&
      fetchedProgramInfo
    ) {
      updateFormData(
        createFormDataFromProgramInfo(
          programInfo,
          programId,
          selectedGroups,
          unSelectedGroups,
          selectedWorkspace,
          currentFlow,
        ),
      );
    }
  }, [fetchingProgramInfo]);

  const updateFormData = newData => {
    setFormData(oldData => ({
      ...oldData,
      ...newData,
    }));
  };

  useEffect(() => {
    if (!programId && groups.length > 0) {
      updateFormData({ unSelectedGroups });
    }
  }, [groups.length]);

  useEffect(() => {
    if (formData?.neverEnd) return;

    const sDate = new Date(`${formData?.startDate}`);
    if (Date.parse(formData?.endDate) < Date.parse(sDate)) {
      updateFormData({ endDate: formatDate(sDate) });
    }
  }, [formData?.startDate]);

  useEffect(() => {
    const { validation_errors: validationErrors = [], program_id: pId } =
      reviewProgramDetails;
    const resolvedProgramId = programId || pId;

    if (flag && !_isEmpty(reviewProgramDetails)) {
      setFlag(false);
      if (validationErrors?.length > 0) {
        updateFormData({ showError: true });
      } else {
        navigate(
          CREATE_POLICY_ROUTE.replace(':programId', resolvedProgramId),
        );
      }
    }
  }, [reviewProgramDetails]);

  const updateSelectedGroups = newGroups => {
    updateFormData({ selectedGroups: newGroups, showError: false });
  };
  const updateUnSelectedGroups = newGroups => {
    updateFormData({ unSelectedGroups: newGroups });
  };

  useEffect(() => {
    const formValid = isFormValid(formData);
    setCreatePolicyEnabled(formValid);
  }, [
    formData?.name,
    formData?.selectedGroups,
    formData?.startDate,
    formData?.startTime,
    formData?.endDate,
    formData?.neverEnd,
  ]);

  const handleNextBtnClick = () => {
    const validStartAndEndDate = validateDates(formData, showErrorToast);
    if (validStartAndEndDate) {
      setFlag(true);
      let programBody = createProgramBody(
        formData,
        programInfo,
        programId,
        selectedWorkspace,
        currentFlow,
      );
      programBody = getProgramBody(programBody);
      reviewProgram({ programBody, id: companyId });
    }
  };

  return (
    <PageWrapper>
      <PageHeaderContainer>
        <PageHeader>
          <PageTitle>Create meal plan</PageTitle>
          <PageSubtitle>Set up a new meal plan for your teams</PageSubtitle>
        </PageHeader>
        <StepBar steps={createPlanSteps} currentStep={1} />
      </PageHeaderContainer>
      <ContentContainer>
        <SectionHeading>Add meal plan details</SectionHeading>
        <PlanSnippet
          formData={formData}
          updateFormData={updateFormData}
          updateSelectedGroups={updateSelectedGroups}
          updateUnSelectedGroups={updateUnSelectedGroups}
          allGroups={allGroups}
          planDataFlag={false}
          adjustBackgroundHeight
        />
      </ContentContainer>
      <FooterWrapper>
        <FullFooter>
          <Button
            appearance='outline'
            icon={<ChevronLeft />}
            iconAlign='left'
            onClick={goBack}
          >
            Go Back
          </Button>
          <Button
            iconAlign='right'
            icon={<ChevronRight />}
            btnColor='red'
            onClick={handleNextBtnClick}
            disabled={!createPolicyEnabled}
            loading={reviewingProgram}
          >
            Setup policy
          </Button>
        </FullFooter>
      </FooterWrapper>
    </PageWrapper>
  );
}

CreatePlan.propTypes = {
  companyId: PropTypes.string,
  currentFlow: PropTypes.number,
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  groupsData: PropTypes.objectOf(PropTypes.any),
  showErrorToast: PropTypes.func,
  reviewProgram: PropTypes.func,
  reviewingProgram: PropTypes.bool,
  reviewProgramDetails: PropTypes.objectOf(PropTypes.any),
  programInfo: PropTypes.objectOf(PropTypes.any),
  getProgramInfo: PropTypes.func,
  fetchingProgramInfo: PropTypes.bool,
  fetchedProgramInfo: PropTypes.bool,
  getAllGroups: PropTypes.func,
  clearProgramInfo: PropTypes.func,
};

CreatePlan.defaultProps = {
  companyId: '',
  currentFlow: 0,
  selectedWorkspace: {},
  groupsData: {},
  reviewProgramDetails: {},
  reviewProgram: _noop,
  reviewingProgram: false,
  programInfo: {},
  getProgramInfo: _noop,
  fetchedProgramInfo: false,
  fetchingProgramInfo: false,
  getAllGroups: _noop,
  showErrorToast: _noop,
  clearProgramInfo: _noop,
};

export default CreatePlan;
